// 0130 · AUDITORÍA DEL TURNO: operación por operación, quién la hizo (persona), desde qué equipo e IP
// (la sesión con que entró: de ahí se deriva el puesto, mientras no exista org.puesto) y cuándo. V3: la
// caja y el turno van aparte, en la cabecera; persona, caja, turno y sesión no se mezclan. Responde las
// preguntas de Rodrigo:
// quién abrió y cerró, quién habilitó a quién, quién vendió, puso el precio manual, cobró, recibió el
// efectivo, anuló y movió efectivo, y cómo terminó la excepción. Es de supervisión (el cajero cierra a
// ciegas): la ven caja.turno.ver / auditoria.ver, y quien resuelve la excepción de un turno ya cerrado.
import { useAuditoriaTurno } from '../api/consultas';
import type { OperacionAuditoriaTurno } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fechaHora, pesos } from '../comun/formato';
import { NOMBRE_MEDIO_CAJA } from './MiTurno';
import { motivosEnTexto, NOMBRE_CAUSA, NOMBRE_RESOLUCION, senalesEnTexto } from './textos';

export const NOMBRE_OPERACION: Record<string, string> = {
  APERTURA: 'Apertura',
  OPERADOR_AUTORIZADO: 'Habilitó operador',
  OPERADOR_RETIRADO: 'Retiró operador',
  VENTA_CREADA: 'Venta',
  VENTA_CONFIRMADA: 'Venta confirmada',
  PRECIO_MANUAL: 'Precio manual',
  VENTA_ANULADA: 'Venta anulada',
  COBRO: 'Cobro',
  SOLICITUD_ANULACION: 'Pidió anular',
  SOLICITUD_RECHAZADA: 'Rechazó la anulación',
  COBRO_ANULADO: 'Cobro anulado',
  MOVIMIENTO: 'Retiro / ingreso',
  BRECHA_RUTA: 'Plata de ruta sin entrar',
  CIERRE: 'Cierre y arqueo',
  EXCEPCION_CREADA: 'Excepción de cierre',
  EXCEPCION_ESCALADA: 'Excepción escalada',
  EXCEPCION_RESUELTA: 'Excepción resuelta',
  REVISION: 'Revisión',
};

const CAUSA_RETIRO: Record<string, string> = {
  RETIRADO: 'la responsable lo sacó',
  CIERRE_TURNO: 'cierre del turno',
  ELEGIBILIDAD_RETIRADA: 'administración le quitó la elegibilidad',
};

const nombreDe = (v: unknown) => (v as { nombre?: string } | null)?.nombre ?? '—';

/** CASH_SOURCE: de dónde viene la plata del cobro. */
const ORIGEN: Record<string, string> = {
  POS_SALE: 'venta del mesón',
  ROUTE_COLLECTION: 'plata de ruta',
  CUSTOMER_PAYMENT: 'abono de cliente',
  OTHER: 'otro',
};

/** Lo esencial de cada operación, en palabras. */
function detalleOperacion(o: OperacionAuditoriaTurno): string {
  const d = o.detalle;
  switch (o.tipo) {
    case 'APERTURA':
      return `fondo ${pesos(String(d['fondoInicial']))}${d['automatico'] ? ' · se abrió sola' : ''}${
        Array.isArray(d['sugeridos'])
          ? ` · sugeridos: ${(d['sugeridos'] as unknown[]).map(nombreDe).join(', ')}`
          : ''
      }${d['legado'] ? ' · cuenta compartida de puesto: persona desconocida' : ''}`;
    case 'SOLICITUD_ANULACION':
      return `${NOMBRE_CAUSA[d['causa'] as keyof typeof NOMBRE_CAUSA] ?? String(d['causa'])}${d['nota'] ? ` · «${String(d['nota'])}»` : ''}`;
    case 'SOLICITUD_RECHAZADA':
      return d['nota'] ? `«${String(d['nota'])}»` : 'sin nota';
    case 'BRECHA_RUTA':
      return `${d['tipo'] === 'FALTANTE' ? 'faltante' : 'sobrante'} ${pesos(String(d['monto']))} · ${String(d['detalle'])}`;
    case 'OPERADOR_AUTORIZADO':
      return `${nombreDe(d['operador'])}${d['excepcional'] ? ' · excepcional' : ''} (elegible desde ${fechaHora(String(d['elegibleDesde']))}, por ${nombreDe(d['elegiblePor'])})`;
    case 'OPERADOR_RETIRADO':
      return `${nombreDe(d['operador'])} · ${CAUSA_RETIRO[String(d['causa'])] ?? String(d['causa'])}`;
    case 'PRECIO_MANUAL':
      return `línea ${String(d['linea'])} ${String(d['sku'])} a ${pesos(String(d['precio']))}${d['referencia'] ? ` (lista ${pesos(String(d['referencia']))})` : ''}${d['motivo'] ? ` · «${String(d['motivo'])}»` : ''}`;
    case 'COBRO': {
      const medios = (d['medios'] as { medio: string; monto: string }[])
        .map(
          (m) =>
            `${NOMBRE_MEDIO_CAJA[m.medio as keyof typeof NOMBRE_MEDIO_CAJA] ?? m.medio} ${pesos(m.monto)}`,
        )
        .join(' + ');
      return `${pesos(String(d['total']))}${d['nota'] ? ` · ${String(d['nota'])}` : ''} · ${medios}${d['origen'] ? ` · ${ORIGEN[String(d['origen'])] ?? String(d['origen'])}` : ''}${d['estado'] === 'ANULADO' ? ' · anulado' : ''}`;
    }
    case 'COBRO_ANULADO':
      return `${pesos(String(d['total']))}${d['correccion'] ? ' · corrección de medios' : d['solicitudId'] ? ' · con solicitud' : ' · sin solicitud'}${d['motivo'] ? ` · «${String(d['motivo'])}»` : ''}`;
    case 'MOVIMIENTO':
      return `${d['tipo'] === 'RETIRO' ? 'retiro' : 'ingreso'} ${pesos(String(d['monto']))} · ${String(d['motivo'])}`;
    case 'CIERRE': {
      const senales = Array.isArray(d['senales'])
        ? (d['senales'] as { codigo: string }[]).map((s) => s.codigo)
        : [];
      return `${d['cuadra'] ? 'cuadra' : `diferencia neta ${pesos(String(d['diferencia']))}`}${senales.length ? ` · señales: ${senalesEnTexto(senales)}` : ''}`;
    }
    case 'EXCEPCION_CREADA':
      return `${d['origen'] === 'POST_CLOSE_VOID_EXCEPTION' ? 'anulada después del cierre · ' : d['origen'] === 'CORRECCION_POSTERIOR' ? 'corrección posterior al cierre · ' : ''}nivel ${String(d['nivel'])} · ${motivosEnTexto(d['motivos'] as string[])}`;
    case 'EXCEPCION_ESCALADA':
      return `a nivel 2${d['nota'] ? ` · «${String(d['nota'])}»` : ''}`;
    case 'EXCEPCION_RESUELTA':
      return `${d['causa'] ? `${NOMBRE_CAUSA[d['causa'] as keyof typeof NOMBRE_CAUSA] ?? String(d['causa'])} · ` : ''}${NOMBRE_RESOLUCION[d['resolucion'] as keyof typeof NOMBRE_RESOLUCION] ?? String(d['resolucion'])}${d['nota'] ? ` · «${String(d['nota'])}»` : ''}`;
    case 'REVISION':
      return String(d['resultado'] ?? '')
        .toLowerCase()
        .replace(/_/g, ' ');
    default:
      return '';
  }
}

export function TablaAuditoriaTurno({ operaciones }: { operaciones: OperacionAuditoriaTurno[] }) {
  return (
    <table className="grilla compacta" aria-label="Operaciones del turno">
      <thead>
        <tr>
          <th>Hora</th>
          <th>Operación</th>
          <th>Documento</th>
          <th>Persona</th>
          <th>Sesión (equipo · IP)</th>
          <th>Detalle</th>
        </tr>
      </thead>
      <tbody>
        {operaciones.map((o, i) => (
          <tr key={`${o.tipo}-${i}`} data-operacion={o.tipo}>
            <td>{fechaHora(o.en)}</td>
            <td>{NOMBRE_OPERACION[o.tipo] ?? o.tipo}</td>
            <td className="mono">{o.referencia}</td>
            <td>{o.operador?.nombre ?? 'sistema'}</td>
            <td>
              {o.sesion
                ? `${o.sesion.dispositivo ?? 'equipo sin nombre'} · ${o.sesion.ip ?? 'sin IP'}`
                : '—'}
            </td>
            <td>{detalleOperacion(o)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AuditoriaTurno({
  empresaId,
  turnoId,
  alCerrar,
}: {
  empresaId: string;
  turnoId: string;
  alCerrar: () => void;
}) {
  const q = useAuditoriaTurno(empresaId, turnoId);
  const a = q.data;
  return (
    <Modal
      titulo={`Auditoría del turno ${a?.turno.numeroVisible ?? ''}`}
      ancho="ancho"
      alCerrar={alCerrar}
      pie={
        <button type="button" onClick={alCerrar}>
          Cerrar
        </button>
      }
    >
      {q.isError && <PanelError error={q.error} />}
      {a && (
        <>
          <p data-cabecera-turno>
            Turno <b>{a.turno.numeroVisible}</b> · caja <b>{a.turno.caja.codigo}</b> · responsable
            del turno <b>{a.turno.responsable.nombre}</b>
            {a.turno.compartido ? ' · turno compartido' : ''}
            {a.turno.calidadIdentidad === 'PERSONAL_ATTRIBUTED' && ' · personal atribuido'}
            {a.turno.legado
              ? ` · legado (LEGACY_SHARED): cuenta compartida de puesto (${a.turno.legado.cuenta}), persona responsable desconocida`
              : ''}
            {a.turno.cierreClase === 'NORMAL' && ' · cierre normal'}
            {a.turno.excepcion &&
              ` · excepción de nivel ${a.turno.excepcion.nivel} (${a.turno.excepcion.estado === 'ABIERTA' ? 'abierta' : 'resuelta'})`}
          </p>
          <section className="recuadro" aria-label="Por persona">
            <h3>Por persona</h3>
            <table className="grilla compacta">
              <thead>
                <tr>
                  <th>Persona</th>
                  <th className="al-der">Cobros</th>
                  <th className="al-der">Cobrado</th>
                  <th className="al-der">Efectivo recibido</th>
                  <th className="al-der">Anulaciones</th>
                </tr>
              </thead>
              <tbody>
                {a.porPersona.map((p) => (
                  <tr key={p.persona.id}>
                    <td>{p.persona.nombre}</td>
                    <td className="al-der">{p.cobros}</td>
                    <td className="al-der">{pesos(p.cobrado)}</td>
                    <td className="al-der">{pesos(p.efectivo)}</td>
                    <td className="al-der">{p.anulaciones}</td>
                  </tr>
                ))}
                {!a.porPersona.length && (
                  <tr>
                    <td colSpan={5} className="grilla-vacia">
                      Sin cobros en este turno
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
          <section className="recuadro" aria-label="Operaciones">
            <h3>Operaciones</h3>
            <TablaAuditoriaTurno operaciones={a.operaciones} />
          </section>
        </>
      )}
    </Modal>
  );
}
